import React, { useCallback, useEffect, useRef, useState } from 'react';
import CanonicalSessionPanel from './CanonicalSessionPanel';
import { SESSION_STATE as S } from '../../../chargerUx/canonical';
import { copyFor } from '../../../chargerUx/copy';
import { onApplied } from '../../../chargerUx/canonicalStore';
import { useCanonicalSession } from '../../../chargerUx/useCanonicalSession';
import { markCanonicalViewed } from '../../../api/canonicalSessionApi';
import { useOptionalToast } from '../../../context/ToastContext';

const RECOVERED_FROM = new Set([S.PAUSED, S.RESUMING]);

/**
 * The live status card on the active-session screen, for a session whose
 * canonical snapshot the API provides. Renders nothing otherwise.
 *
 *   - updates in place as newer snapshots arrive (stream, poll, foreground);
 *   - a recovery (paused → charging, contact restored) shows one transient
 *     confirmation — once per applied snapshot, never for a duplicate;
 *   - an ended, failed or safety outcome stays as a persistent panel;
 *   - tells the API which version the client has now seen, so in-app notices
 *     and badges clear and a redundant push is not sent for it.
 *
 * Props:
 *   sessionUid
 *   onRefresh   async () => any — fetch the authoritative session (manual retry)
 *   onAction    (actionCode) => void
 */
function LiveCanonicalStatus({ sessionUid, onRefresh = null, onAction = null }) {
  const { snapshot, stale, receivedAt } = useCanonicalSession(sessionUid);
  const toast = useOptionalToast();
  const showToast = toast?.showToast;
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // Transient confirmation for recovery events.
  useEffect(() => {
    if (!sessionUid) return undefined;
    const c = copyFor();
    return onApplied((next, previous) => {
      if (!previous || next.sessionUid !== String(sessionUid).toLowerCase()) return;
      if (next.sessionState !== S.CHARGING) return;
      if (RECOVERED_FROM.has(previous.sessionState)) showToast?.({ message: c.recovered, type: 'success' });
      else if (previous.sessionState === S.STATUS_UNKNOWN) showToast?.({ message: c.restored, type: 'info' });
    });
  }, [sessionUid, showToast]);

  // Acknowledge each version once.
  const viewed = useRef(0);
  const version = snapshot?.version ?? null;
  useEffect(() => {
    if (!sessionUid || version == null || version <= viewed.current) return;
    viewed.current = version;
    markCanonicalViewed(sessionUid, version);
  }, [sessionUid, version]);

  const refresh = useCallback(async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      const result = await onRefresh();
      // The last confirmed state stays on screen; a failed refresh only labels it.
      setRefreshFailed(result === false || result?.ok === false || result?.success === false);
    } catch {
      setRefreshFailed(true);
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh, refreshing]);

  // A newer snapshot is proof the connection works again.
  useEffect(() => { setRefreshFailed(false); }, [version]);

  if (!snapshot) return null;
  return (
    <CanonicalSessionPanel
      snapshot={snapshot}
      stale={stale}
      receivedAt={receivedAt}
      refreshFailed={refreshFailed}
      refreshing={refreshing}
      onRefresh={onRefresh ? refresh : null}
      onAction={onAction}
    />
  );
}

export default LiveCanonicalStatus;
